import '/css/main.scss';
import 'bootstrap-icons/font/bootstrap-icons.css';

import * as bootstrap from 'bootstrap';
import $ from 'jquery';

// Expose jQuery globally so Bootstrap's event bridge triggers jQuery-bound
// handlers ($(el).on('shown.bs.modal', ...)). Bundled ES-module jQuery never
// sets window.jQuery itself, and without it Bootstrap only dispatches native
// events — silently skipping every jQuery listener in the app.
window.$ = window.jQuery = $;

import * as Sentry from "@sentry/browser";
import posthog from 'posthog-js';

Sentry.init({
  dsn: "https://2a83272742414f1bb70a89b9f75e79ba@o4504990620909568.ingest.sentry.io/4504990623727616",
  environment: import.meta.env.ENV || 'production',
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  integrations: [new Sentry.Replay({maskAllText: false, maskAllInputs: false, blockAllMedia: false}), new Sentry.BrowserTracing()],
  // Sample a fraction of transactions — 100% tracing adds overhead to every
  // request and burns Sentry quota without adding much signal.
  tracesSampleRate: 0.2,
});

posthog.init('phc_ve6TTtNtPPPBpxhyK6XS7E5owgVgyegy9PNcIoNdEzH',
    {
        api_host: 'https://i.stagevault.com',
        person_profiles: 'identified_only', // or 'always' to create profiles for anonymous users as well
        // Sentry catches uncaught errors and unhandled rejections the moment
        // it initializes; PostHog does not unless asked. Off by default, both
        // platforms would see a different set of errors.
        capture_exceptions: true
    }
)

// Sentry scopes every event to an environment at init. PostHog has no
// equivalent, so register the same fields as super properties — they ride
// along on every event and can be filtered the same way.
registerEnvironment({ app: 'client', environment: import.meta.env.ENV || 'production', version: APP_VERSION });

import { renderLibrary } from '/js/library.js';
import { authStatus, promptSignIn, userData, updateUserData } from '/js/accounts.js';
import { loader, showLoader, hideLoader } from '/js/navigation.js';
import { applyRoute } from '/js/router.js';
import { registerEnvironment } from '@shared/js/analytics.js';
import { svInit, APP_VERSION } from '@shared/js/instance.js';
import { v1BaseUrl } from '/js/config.js';
import { escapeHtml, mkToast } from '@shared/js/toasts.js';

svInit();

const urlParams = new URLSearchParams(window.location.search);
const manualActivate = window.location.pathname === '/activate' && !urlParams.get('code');

let pendingAuthCode = null;
let authModalShown = false;
export var libraryRendered = false;
export function markLibraryRendered() { libraryRendered = true; }
export function resetLibraryRendered() { libraryRendered = false; }

if (manualActivate) {
  hideLoader();
  renderActivationCodeEntry();
} else {
  if (urlParams.get('action') === 'authorizeDevice' && urlParams.get('code')) {
    // Sanitize activation code from query string
    const rawCode = urlParams.get('code') || '';
    pendingAuthCode = rawCode.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  }
  showLoader();
  // Authentication check called right away and recursively
  setTimeout(() => persistentAuthCheckCallback(true), 0);
}

// Re-validate the session on a slow interval. Background tabs skip the check
// entirely (see the visibilitychange listener below, which re-checks the
// moment the tab is foregrounded again) — this keeps a stale session from
// lingering without hammering the API every few seconds.
const AUTH_CHECK_INTERVAL_MS = 60000;

function persistentAuthCheckCallback(force = false) {
  if (force || !document.hidden) {
    authStatus(authCheckCallback);
  }
  setTimeout(persistentAuthCheckCallback, AUTH_CHECK_INTERVAL_MS);
}

// Check immediately when the user returns to the tab — e.g. after verifying
// their email or signing out elsewhere — instead of waiting for the next tick.
document.addEventListener('visibilitychange', () => {
  if (!document.hidden && !manualActivate) {
    authStatus(authCheckCallback);
  }
});

function authCheckCallback(isLoggedIn) {
  if (isLoggedIn && !libraryRendered) {
        updateUserData()
          .then(() => {
                hideLoader();
                // Let the router decide what to render based on the URL —
                // e.g. /shows/abc opens the details page, /shows/abc/watch
                // starts the player. Default route is the vault.
                applyRoute();
                markLibraryRendered();
          })
          .catch(() => {
                console.error("Error fetching account info");
          });

	if ($("#authPrompt").length == 1) {
	  // destroySignInPrompt();
	}
  } else if (!isLoggedIn) {
        if ($("#authPrompt").length == 0) {
          promptSignIn(pendingAuthCode);
        }
        resetLibraryRendered();
  }

  handleAuthorizeFlow(isLoggedIn);
}


function handleAuthorizeFlow(isLoggedIn) {
  if (!pendingAuthCode || authModalShown) return;

  if (isLoggedIn) {
    showAuthorizeModal(pendingAuthCode);
    authModalShown = true;
  } else {
    if ($('#deviceAuthNotice').length === 0 && $('#login').length) {
      const safeCode = escapeHtml(pendingAuthCode);
      $('#login').prepend(`<div id="deviceAuthNotice" class="card card-body text-center mb-3">Log in to authorize device <strong>${safeCode}</strong></div>`);
    }
  }
}

async function showAuthorizeModal(code) {
  const safeCode = escapeHtml(code);
  const modal = $(
    `<div class="modal fade" id="authorizeDeviceModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Authorize This Device?</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body text-center">
            <p>Grant this device access to your Stage Vault account?</p>
            <p class="mb-3">Code <strong>${safeCode}</strong></p>
            <div class="mt-3">
              <button id="approveDevice" class="btn btn-primary me-2">Approve</button>
              <button id="denyDevice" class="btn btn-secondary">Deny</button>
            </div>
          </div>
        </div>
      </div>
    </div>`
  );
  $('body').append(modal);
  const modalEl = document.getElementById('authorizeDeviceModal');
  const modalInstance = new bootstrap.Modal(modalEl);
  modalInstance.show();

  $('#approveDevice').on('click', () => sendDecision('approve'));
  $('#denyDevice').on('click', () => sendDecision('deny'));

  // Send the user's approve/deny decision to the backend
  async function sendDecision(action) {
    try {
      const res = await fetch(v1BaseUrl + 'auth/deviceRespond', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ code, action })
      });
      if (res.ok) {
        mkToast('device', 'success', 'Success', action === 'approve' ? 'Device approved' : 'Device denied');
        history.replaceState(null, '', '/');
      } else {
        mkToast('device', 'danger', 'Error', 'Unable to submit response');
      }
    } catch (err) {
      mkToast('device', 'danger', 'Error', 'Request failed');
    } finally {
      modalInstance.hide();
      pendingAuthCode = null;
    }
  }

  $(modalEl).on('hidden.bs.modal', function(){ $(modalEl).remove(); });
}

function renderActivationCodeEntry() {
  const html = `
    <div class="container mt-5">
      <div class="card mx-auto" style="max-width:400px;">
        <div class="card-body text-center">
          <h5 class="card-title mb-3">Enter Device Code</h5>
          <input id="activationCode" class="form-control text-uppercase text-center" maxlength="6" autocomplete="off" />
          <div id="activationAlert" class="alert alert-danger mt-3 d-none"></div>
          <button id="submitActivation" class="btn btn-primary w-100 mt-3">Submit</button>
        </div>
      </div>
    </div>`;
  $('#app').html(html);
  $('#activationCode').on('input', function(){ this.value = this.value.toUpperCase().replace(/[^A-Z0-9]/g,''); });
  $('#submitActivation').on('click', async () => {
    const code = $('#activationCode').val().trim().toUpperCase();
    const res = await fetch(v1BaseUrl + 'device/lookup?code=' + code, { method: 'POST' });
    if (res.ok) {
      window.location = '/?action=authorizeDevice&code=' + code;
    } else if (res.status === 404) {
      $('#activationAlert').text('Code not found or expired').removeClass('d-none');
    } else {
      $('#activationAlert').text('Error checking code').removeClass('d-none');
    }
  });
}
